/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppSelector } from '../../../../store';
import { FeatureFlag } from '../../../../ts/feature-flags';
import { selectDraftEnvironmentId } from '../../../ia/store/environments/selectors';
import { isFeatureEnabled } from '../../../ia/store/features/selectors';
import { useChannelForm } from '../../hooks/useChannelForm';
import { CATALOG_CHANNEL_TYPE } from '../../util/channelTypes';
import { ChannelProps } from '../../util/types';
import { TabbedHeader } from '../common/tabbed-header/TabbedHeader';
import { AuthenticationTab } from './authentication-tab/AuthenticationTab';
import { MappingTab } from './mapping-tab/MappingTab';
import { CatalogOpenApiMessages as messages } from './messages';
import { CatalogOpenApiQuickSetup } from './new/CatalogOpenApiQuickSetup';
import { CatalogOpenApiSplashPage } from './new/CatalogOpenApiSplashPage';
import { settingsSchema } from './schema';

import './CatalogOpenApi.scss';

export const CatalogOpenApi = ({
  assistantId,
  environmentId,
  integrationId,
  catalogItemId,
  isNewClassicIntegration,
  defaultTab
}: ChannelProps) => {
  const { formatMessage: t } = useIntl();
  const {
    loading,
    initialName,
    updateIntegrationName,
    formMethods,
    useQuickSetup,
    isReadOnly
  } = useChannelForm(
    CATALOG_CHANNEL_TYPE,
    assistantId,
    environmentId,
    integrationId,
    settingsSchema,
    catalogItemId
  );

  const isUpdateExtensionEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.INTEGRATIONS_CUSTOM_UPDATE)
  );
  const draftEnvironmentId = useAppSelector(selectDraftEnvironmentId);
  const isDraftExtension = environmentId === draftEnvironmentId;

  return (
    <FormProvider {...formMethods}>
      <TabbedHeader
        assistantId={assistantId}
        environmentId={environmentId}
        catalogItemId={catalogItemId}
        channelType={CATALOG_CHANNEL_TYPE}
        integrationName={initialName}
        updateIntegrationName={updateIntegrationName}
        useSplashPage={isNewClassicIntegration}
        useQuickSetup={useQuickSetup}
        loading={loading}
        QuickSetup={<CatalogOpenApiQuickSetup />}
        SplashPage={<CatalogOpenApiSplashPage />}
        Tabs={[
          <MappingTab
            key="tab-1"
            environmentId={environmentId}
            tabname={
              isUpdateExtensionEnabled && isDraftExtension
                ? t(messages.manageExtension)
                : t(messages.reviewExtension)
            }
            catalogItemId={catalogItemId}
          />,
          <AuthenticationTab
            key="tab-2"
            tabname={t(messages.authenticationTab)}
            catalogItemId={catalogItemId}
            environmentId={environmentId}
          />
        ]}
        defaultTab={defaultTab}
        readOnly={isReadOnly}
      />
    </FormProvider>
  );
};
